import {
  isPlainObject, toLower, each, filter, isNil,
} from 'lodash';
import eventHelpers from '@/services/helpers/event';
import Event from './event';

const calculateTieBreakPoints = (period, side) => {
  const tieBreak = period?.[13];
  return filter(tieBreak, (point) => point === side).length;
};

const calculateCurrentGameScore = (currentGameScore) => {
  const homeScore = currentGameScore?.HOME === 50 ? 'A' : currentGameScore?.HOME ?? 0;
  const awayScore = currentGameScore?.AWAY === 50 ? 'A' : currentGameScore?.AWAY ?? 0;
  return {
    HOME: homeScore,
    AWAY: awayScore,
  };
};

const TennisPerPeriod = (perPeriod, {
  side, type, pointsWonPerGame, currentPeriod,
}) => {
  const opponent = side === 'HOME' ? 'AWAY' : 'HOME';

  const calculateValues = (period, periodKey) => {
    if (type !== 'games') {
      if (!isNil(period?.[side]?.PLAYER_A)) return period?.[side]?.PLAYER_A;
      return period?.[side] ?? 0;
    }
    if ((period?.[side] === 6 || period?.[side] === 7) && (period?.[opponent] === 6 || period?.[opponent] === 7)) {
      return periodKey === currentPeriod
        ? period?.[side]
        : `${period?.[side]}(${calculateTieBreakPoints(pointsWonPerGame?.[periodKey], side)})`;
    }
    return period?.[side] ?? 0;
  };

  let scoresPerPeriod = {};
  each(perPeriod, (period, key) => {
    scoresPerPeriod = {
      ...scoresPerPeriod,
      [key]: calculateValues(period, key),
    };
  });
  return scoresPerPeriod;
};

const TennisCompetitor = (event, { side, details }) => {
  const competitor = event?.[toLower(side)];
  if (!isPlainObject(competitor)) return null;

  return {
    ...competitor,
    score: details?.setsWon?.[side] || 0,
    scorePerPeriod: TennisPerPeriod(details?.gamesWonPerSet, {
      side, type: 'games', pointsWonPerGame: details?.pointsWonPerGame, currentPeriod: details?.period,
    }),
    aces: details?.acesInMatch?.[side] || 0,
    acesPerPeriod: TennisPerPeriod(details?.acesPerSet, { side }),
    doubleFaults: details?.doubleFaultsInMatch?.[side] || 0,
    doubleFaultsPerPeriod: TennisPerPeriod(details?.doubleFaultsPerSet, { side }),
    breakPointsPlayedInMatch: details?.breakPointsPlayedInMatch?.[side] || 0,
    breakPointsWonInMatch: details?.breakPointsWonInMatch?.[side] || 0,
    breakPointsPlayedPerPeriod: TennisPerPeriod(details?.breakPointsPlayedPerSet, { side }),
    breakPointsWonPerPeriod: TennisPerPeriod(details?.breakPointsWonPerSet, { side }),
    breakPointsWonPercentageInMatch: details?.breakPointsWonPercentageInMatch?.[side] || 0,
    breakServePointsWonPercentagePerPeriod: TennisPerPeriod(details?.breakServePointsWonPercentagePerSet, { side }),
    firstServePercentageInMatch: details?.firstServePercentageInMatch?.[side] || 0,
    firstServePercentagePerPeriod: TennisPerPeriod(details?.firstServePercentagePerSet, { side }),
    firstServePointsPlayedInMatch: details?.firstServePointsPlayedInMatch?.[side] || 0,
    firstServePointsPlayedPerPeriod: TennisPerPeriod(details?.firstServePointsPlayedPerSet, { side }),
    firstServePointsWonInMatch: details?.firstServePointsWonInMatch?.[side] || 0,
    firstServePointsWonPerPeriod: TennisPerPeriod(details?.firstServePointsWonPerSet, { side }),
    firstServePointsWonPercentageInMatch: details?.firstServePointsWonPercentageInMatch?.[side] || 0,
    firstServePointsWonPercentagePerPeriod: TennisPerPeriod(details?.firstServePointsWonPercentagePerSet, { side }),
    secondServePercentageInMatch: details?.secondServePercentageInMatch?.[side] || 0,
    secondServePercentagePerPeriod: TennisPerPeriod(details?.secondServePercentagePerSet, { side }),
    secondServePointsPlayedInMatch: details?.secondServePointsPlayedInMatch?.[side] || 0,
    secondServePointsPlayedPerPeriod: TennisPerPeriod(details?.secondServePointsPlayedPerSet, { side }),
    secondServePointsWonInMatch: details?.secondServePointsWonInMatch?.[side] || 0,
    secondServePointsWonPerPeriod: TennisPerPeriod(details?.secondServePointsWonPerSet, { side }),
    secondServePointsWonPercentageInMatch: details?.secondServePointsWonPercentageInMatch?.[side] || 0,
    secondServePointsWonPercentagePerPeriod: TennisPerPeriod(details?.secondServePointsWonPercentagePerSet, { side }),
  };
};

export default (data, selectedFeed) => {
  if (!isPlainObject(data)) return null;
  const event = Event(data, selectedFeed);
  const details = eventHelpers.findEventDetails(data, selectedFeed);

  return {
    ...event,
    period: details?.period || 'N_A',
    periods: details?.setsToPlay || 0,
    matchTime: details?.matchTime || 0,
    setDurations: details?.setDurations || [],
    teamInPossession: details?.currentServingTeam || 'NEUTRAL',
    home: TennisCompetitor(event, { side: 'HOME', details }),
    away: TennisCompetitor(event, { side: 'AWAY', details }),
    currentGameScore: calculateCurrentGameScore(details?.currentGameScore),
    currentGameNumber: details?.currentGameNumber,
    isPointInProgress: details?.isPointInProgress,
    pointsWonPerGame: details?.pointsWonPerGame,
    secondServe: details?.secondServe,
    noAdvantage: details?.noAdvantage,
    setsToPlay: details?.setsToPlay,
    lastSetSuperTiebreak: details?.lastSetSuperTiebreak,
    lastSetSuperTiebreakPoints: details?.lastSetSuperTiebreakPoints,
    inBreak: details?.eventInBreak,
    inPrematch: details?.eventPreMatch,
    riskFlags: details?.riskFlags || [],
    isDoubles: details?.isDoubles || false,
  };
};
